/**
 * When the page tries to drain (v3 section 10):
 *   - on `online`
 *   - on `visibilitychange` to visible
 *   - on a timer while foregrounded
 *   - and, from the service worker, on the Background Sync `sync` event that
 *     enqueueCompletion registers (engine.requestBackgroundSync).
 *
 * Plus, on first run, navigator.storage.persist() so Chrome does not evict
 * the queue under storage pressure.
 */
import { setMeta } from './db'
import { drain } from './engine'

export const FOREGROUND_INTERVAL_MS = 30_000

/**
 * Ask until it is granted. Chrome decides persist() silently, with no prompt,
 * from signals such as "installed to the home screen", so the FIRST call -
 * often made in a plain browser tab before the app is installed - is usually
 * refused. The earlier version recorded that refusal and never asked again,
 * which left an installed phone non-persistent for good. Now: while the origin
 * is not persisted, ask again on every start; once Chrome agrees, stop.
 * Found 2026-09-14 writing the device checklist for Tom's trial.
 */
export async function requestPersistentStorage(): Promise<boolean | null> {
  const storage = (globalThis.navigator as Navigator | undefined)?.storage
  if (!storage || typeof storage.persist !== 'function') return null
  if (storage.persisted && (await storage.persisted())) {
    await setMeta('persistRequested', true)
    await setMeta('persistGranted', true)
    return true
  }
  const granted = await storage.persist()
  await setMeta('persistRequested', true)
  await setMeta('persistGranted', granted)
  return granted
}

export function installForegroundTriggers(): () => void {
  const kick = () => {
    void drain({ source: 'foreground' })
  }
  const onVisible = () => {
    if (document.visibilityState === 'visible') kick()
  }
  window.addEventListener('online', kick)
  document.addEventListener('visibilitychange', onVisible)
  const timer = window.setInterval(() => {
    if (document.visibilityState === 'visible') kick()
  }, FOREGROUND_INTERVAL_MS)
  kick()
  return () => {
    window.removeEventListener('online', kick)
    document.removeEventListener('visibilitychange', onVisible)
    window.clearInterval(timer)
  }
}
